'use client';

import { RefObject, useEffect } from 'react';

// MediaQueryList Event based useEventListener interface
function useEventListener<K extends keyof MediaQueryListEventMap>(
  eventName: K,
  handler: (event: MediaQueryListEventMap[K]) => void,
  element: RefObject<MediaQueryList | null>,
  options?: boolean | AddEventListenerOptions,
): void

// Window Event based useEventListener interface
function useEventListener<K extends keyof WindowEventMap>(
  eventName: K,
  handler: (event: WindowEventMap[K]) => void,
  element?: undefined,
  options?: boolean | AddEventListenerOptions,
): void

/**
 * Element Event based useEventListener interface.
 *
 * Make sure to wrap useCallback when passing the handler to this hook!
 */
function useEventListener<
  K extends keyof HTMLElementEventMap,
  T extends HTMLElement = HTMLDivElement,
>(
  eventName: K,
  handler: (event: HTMLElementEventMap[K]) => void,
  element: RefObject<T | null>,
  options?: boolean | AddEventListenerOptions,
): void

// Document Event based useEventListener interface
function useEventListener<K extends keyof DocumentEventMap>(
  eventName: K,
  handler: (event: DocumentEventMap[K]) => void,
  element: RefObject<Document | null>,
  options?: boolean | AddEventListenerOptions,
): void

function useEventListener<
  KW extends keyof WindowEventMap,
  KH extends keyof HTMLElementEventMap,
  KM extends keyof MediaQueryListEventMap,
  T extends HTMLElement | MediaQueryList | void = void,
>(
  eventName: KW | KH | KM,
  handler: (
    event:
      | WindowEventMap[KW]
      | HTMLElementEventMap[KH]
      | MediaQueryListEventMap[KM]
      | Event,
  ) => void,
  element?: RefObject<T | null>,
  options?: boolean | AddEventListenerOptions,
) {
  useEffect(() => {
    // Window listener only when no element ref is passed. If a ref is passed but `.current`
    // is still null, do not fall back to `window` — the effect does not re-run when `.current`
    // is assigned later, which previously attached handlers to the whole document (see SearchInputWithResults).
    if (element !== undefined) {
      const el = element.current;
      if (!el?.addEventListener) return undefined;

      const listener: typeof handler = (event) => handler(event);
      el.addEventListener(eventName, listener, options);
      return () => {
        el.removeEventListener(eventName, listener, options);
      };
    }

    const targetElement: Window = window;

    if (!(targetElement && targetElement.addEventListener)) return undefined;

    // Create event listener that calls handler function stored in ref
    const listener: typeof handler = (event) => handler(event);

    targetElement.addEventListener(eventName, listener, options);

    // Remove event listener on cleanup
    return () => {
      targetElement.removeEventListener(eventName, listener, options);
    };
  }, [eventName, element, options, handler]);
}

export {
  useEventListener,
};
